import { useEffect, useRef } from 'react';

/**
 * Sesión: se renueva mientras se usa la app, y si igual se vence en medio de
 * una carga, no se pierde lo cargado.
 *
 * - api.js avisa con EVENTO_SESION_VENCIDA cuando el servidor rechaza una
 *   sesión (401). La ventanita de ModalSesionVencida pide la contraseña.
 * - Al volver a entrar se avisa con EVENTO_SESION_RENOVADA, y la pantalla que
 *   estaba guardando reintenta sola (useReintentoTrasSesion).
 */

export const EVENTO_SESION_VENCIDA = 'evalpro:sesion-vencida';
export const EVENTO_SESION_RENOVADA = 'evalpro:sesion-renovada';

// Pasado un día desde que se emitió, la sesión se renueva al abrir la app.
const RENOVAR_DESPUES_DE_MS = 24 * 60 * 60 * 1000;

/** Datos del token (sin verificar: solo para saber cuándo se emitió). */
export function leerToken(token) {
  try {
    const cuerpo = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(cuerpo));
  } catch {
    return null;
  }
}

/** true si el token ya venció (o no se puede leer). */
export function estaVencido(token, ahora = Date.now()) {
  const datos = token && leerToken(token);
  if (!datos || !datos.exp) return true;
  return datos.exp * 1000 <= ahora;
}

/** true si conviene pedir una sesión nueva. Los tokens viejos no traen iat. */
export function hayQueRenovar(token, ahora = Date.now()) {
  const datos = token && leerToken(token);
  if (!datos) return false;
  if (!datos.iat) return true;
  return ahora - datos.iat * 1000 > RENOVAR_DESPUES_DE_MS;
}

/**
 * Para las pantallas que guardan: devuelve `marcarPendiente`. Si el guardado
 * falló por sesión vencida, se llama a marcarPendiente() y, cuando la persona
 * vuelve a entrar, se reintenta `reintentar` sola, con lo que ya estaba cargado.
 */
export function useReintentoTrasSesion(reintentar) {
  const pendiente = useRef(false);
  const fn = useRef(reintentar);
  fn.current = reintentar;

  useEffect(() => {
    const alRenovar = () => {
      if (!pendiente.current) return;
      pendiente.current = false;
      fn.current();
    };
    window.addEventListener(EVENTO_SESION_RENOVADA, alRenovar);
    return () => window.removeEventListener(EVENTO_SESION_RENOVADA, alRenovar);
  }, []);

  return () => { pendiente.current = true; };
}
